import * as React from "react";
import * as moment from "moment";
import {authorize, IWithRouterProps, Overlay, withRouter} from "@athena-portal/components";
import {
    getAmountToDisplay,
    scrollToTop
} from "../../../../../CommonFunctions";
import {
    EnumInvoiceLineType,
    ICustomerPortalService,
    InvoiceLine,
    PaymentDetails,
    PaymentsResponse,
    Policy,
    productHelper,
    ProductsEnum,
    Subscription,
    Types,
    ValuableItem,
    POLICY_STATUS_MTA,
    POLICY_STATUS_MTC,
    POLICY_STATUS_NEW,
    POLICY_STATUS_REINSTATE,
    POLICY_STATUS_RENEWAL,
    PREMIUM_TYPE_MONTHLY
} from "@falcon/services";
import {lazyInject} from "@athena-portal/core";
import {PluginTemplates} from "../../../../../utils/PluginTemplates";
import {FalconAccordion} from "../../../../../components/FalconAccordion";

interface IPaymentHistoryMainTableParams {
    policyNumber: string;
    [key: string]: string | undefined
}


interface IProps {
    subscription?: Subscription
    showProduct?: string
    paymentType?: string
    title?: string
    paymentPeriod?: PaymentPeriod
    expanded?: boolean
}

interface IState {
    payment?: PaymentsResponse,
    direction: string,
    active: boolean,
    policies?: Policy[]
}

enum PaymentPeriod {
    // payments that belong to policy's current period - from the latest renewal or from policy start if there is no renewal
    current = "current",
    // payments that belong to prev. period - until the latest renewal
    past = "past",
    // all payments
    all = "all"
}

@authorize
export class PaymentHistoryTablePlain extends React.Component<IProps & IWithRouterProps<IPaymentHistoryMainTableParams>, IState> {

    @lazyInject(Types.CustomerPortalService)
    private customerPortalService: ICustomerPortalService;

    static get PaymentPeriod() {
        return PaymentPeriod;
    }

    constructor(props: IProps & IWithRouterProps<IPaymentHistoryMainTableParams>) {
        super(props);

        this.state = {
            direction: 'asc',
            active: false
        };
    }

    public componentDidMount(): void {
        scrollToTop();
        const productToShow = this.props.showProduct;
        this.customerPortalService.getPolicyHistory().subscribe((policies: Policy[]) => {
            const filteredPolicies: Policy[] =  policies?.filter((policy: Policy) => {
                if(productToShow && policy.products.includes(productToShow)) {
                    return true;
                } else if(productToShow === ProductsEnum.SUBSCRIPTION && (policy.products.includes(ProductsEnum.MOTOR) || policy.products.includes(ProductsEnum.RENTERS))) {
                    return true;
                }
                return false;
            });

            this.setState({policies:filteredPolicies});
            const policyNumber = this.props.params.policyNumber;

            switch (this.props.paymentPeriod) {
                case PaymentPeriod.all: {
                    if (policyNumber) {
                        this.customerPortalService.getPaymentHistoryByPolicyNumber(policyNumber).subscribe((payment: PaymentsResponse) => {
                            this.filterPayments(payment, filteredPolicies);
                        });
                    } else {
                        this.customerPortalService.getPaymentHistory().subscribe((payment: PaymentsResponse) => {
                            this.filterPayments(payment, filteredPolicies);
                        });
                    }
                    break;
                }
                case PaymentPeriod.current:
                    this.customerPortalService.getPaymentHistoryCurrentPeriod(policyNumber).subscribe((payment: PaymentsResponse) => {
                        this.filterPayments(payment, filteredPolicies);
                    });
                    break;
                case PaymentPeriod.past:
                    this.customerPortalService.getPaymentHistoryPastPeriod(policyNumber).subscribe((payment: PaymentsResponse) => {
                        this.filterPayments(payment, filteredPolicies);
                    });
                    break;
                default:
                    throw new Error(`Unknown payment period - ${this.props.paymentPeriod}`);
            }
        });
    }

    public render() {
        if (this.state.policies && this.state.payment) {
            return <>{this.renderTable()}</>
        } else {
            return <Overlay show={true}/>
        }
    }

    private renderTable() {
        const paymentHistory =
            <>
                <div className="payment-history grid-col-2 mt-3 margin-top-0" id={this.props.title?.toLowerCase().split(" ").join("_") ?? "payment_history"}>
                    <table className="two-column-table-header">
                        {this.renderTableHeader()}
                    </table>
                    <div className="table-scroll mt-2">
                        <table className="two-column-table-data">
                            {this.renderTableData()}
                        </table>
                    </div>
                </div>
                <div className="payment-history-total-amount grid-col-2">
                    <div className="ph-first-total-amount">
                        Total
                    </div>
                    <div className="ph-second-total-amount">
                        {this.countTotal()}
                    </div>
                </div>
            </>;

        if (this.props.paymentPeriod !== PaymentPeriod.all) {
            return <>
                <div className="panel bg-white overflow-hidden p-0 mx-3 font-weight-extra-light payment-history-all payment-details-section grid-col-2 icon-hand-money-before">
                    <div className="inner-content pb-4">
                        <FalconAccordion isShown={this.props.expanded ?? false} togglePositionTop={true}
                                         toggleHidden={<div
                                             className="toggle d-flex justify-content-between align-items-center history-title">
                                             <span/><span
                                             className="font-weight-bold text-center fs-18" id={(this.props.title?.toLowerCase().split(" ").join("_") ?? "payment_history") + "_section"}>{this.props.title}</span><span
                                             className="toggle-icon expand-icon"/></div>}
                                         toggleShown={<div
                                             className="toggle d-flex justify-content-between align-items-center history-title">
                                             <span/><span
                                             className="font-weight-bold text-center fs-18" id={(this.props.title?.toLowerCase().split(" ").join("_") ?? "payment_history") + "_section"}>{this.props.title}</span><span
                                             className="toggle-icon collapse-icon"/></div>}>
                            {this.hasPaymentHistory() ? paymentHistory :
                                <div className="inner-content text-center mt-2">
                                    You don't have payment history yet!
                                </div>}
                        </FalconAccordion>
                    </div>
                </div>
            </>
        } else {
            if (this.hasPaymentHistory()) {
                return <div className={`payment-details-section icon-hand-money-before`}>
                    <div className="grid-col-2 text-center mt-4 dashboard-panel-title">Payment history</div>
                    {paymentHistory}
                </div>
            } else {
                return <p className="inner-content payment-details-section text-center icon-hand-money-before">You don't have payment history yet!</p>
            }
        }
    }

    private renderTableHeader() {
        return (
            <tr>
                <th>
                    <span className="muli-semi-bold-16">Date</span>
                    <span onClick={this.sortByDate} className={`${this.state.active &&
                    (this.state.direction === "asc" ? "fas fa-sort-up" : "fas fa-sort-down")}
                            ${!this.state.active && "fas fa-sort"} table-sort-arrow`}/>
                </th>
                <th className="muli-semi-bold-16">Paid</th>
            </tr>
        )
    }

    private renderTableData(): React.ReactNode[] {
        const payments: React.ReactNode[] = this.state.payment!.paymentDetailsList.map((payment: PaymentDetails, index: number) => {
            const {date, policyVersion, amount, isPendingPayment, isInFlight} = payment;

            const correspondingPolicies: Policy[] = this.state.policies!.filter((policy: Policy) => {
                for (const pv of policyVersion) {
                    if (pv.id === policy.interaction.agreementVersionId.toString()) {
                        return true;
                    }
                }
                return false;
            });
            const isPaymentChargeback = payment.status === 'CHARGEBACK';
            return (
                <tr key={index}>
                    <td className="muli-semi-bold-16 payment-date">
                        <span className="font-weight-light fs-14">{PluginTemplates.convertDate(date, "DD MMMM YYYY")}</span>
                        <br/>
                        {isPaymentChargeback &&
                            <span className="text-danger fs-14">(Chargeback on {moment(payment.chargebackDate).format("DD.MM.YYYY")})</span>}
                        <div className="payment-description">
                            {!isPaymentChargeback &&
                                <span className="font-weight-regular">{this.getPaymentSummary(payment)}</span>
                            }
                            {(this.props.showProduct !== ProductsEnum.HOME && this.props.paymentType !== PREMIUM_TYPE_MONTHLY) &&
                                <span className="font-weight-light"> for {this.getDescriptions(correspondingPolicies).join(" and ")}</span>
                            }
                        </div>
                        <div>{correspondingPolicies.map((policy: Policy) => {
                            return policy.policyNumber
                        }).join(", ")}</div>
                    </td>
                    <td className="font-weight-semi-bold payment-amount text-nowrap">
                        <span className={isPaymentChargeback ? "text-muted text-decoration-line-through" : ""}>
                            {amount > 0 ? `${getAmountToDisplay(amount)}` : `-${getAmountToDisplay(Math.abs(amount))}`}
                            {isPendingPayment && <span className="d-block pending-payment">Pending</span>}
                            {isInFlight && <span className="d-block in-flight-payment">In-flight</span>}
                        </span>
                        <br/>
                        {isPaymentChargeback && <span>{getAmountToDisplay(0)}</span>}
                    </td>
                </tr>
            )
        });

        if (this.props.subscription && this.props.subscription.nextPaymentAmount) {
            const {nextPaymentAmount, nextPaymentDate} = this.props.subscription;
            payments.unshift(
                <tr key={this.state.payment!.paymentDetailsList.length}>
                    <td id="payment-date" className="muli-semi-bold-16">
                        <span className="font-weight-extra-light fs-14">{PluginTemplates.convertDate(nextPaymentDate, "DD MMMM YYYY")}</span>
                        <br/>
                        Renew payment
                    </td>
                    <td id="payment-price" className="font-weight-semi-bold">
                        {getAmountToDisplay(nextPaymentAmount)}
                        <span className="d-block pending-payment">Pending</span>
                    </td>
                </tr>
            );
        }

        return payments;
    }

    private getPaymentSummary = (paymentDetails: PaymentDetails): string | undefined => {
        if (!paymentDetails.policyVersion || paymentDetails.policyVersion.length === 0) {
            return;
        }

        const paymentDetailsSummary = paymentDetails.policyVersion[0];
        const policyStatus = paymentDetailsSummary.summary;

        if (paymentDetails.invoiceLines?.find(il => il.description?.includes("PayOffBalance"))) {
            return "Balance paid off"
        }

        switch (policyStatus) {
            case POLICY_STATUS_NEW:
            case POLICY_STATUS_MTA:
                return this.getStatusPaymentSummary(paymentDetails);
            case POLICY_STATUS_RENEWAL:
                return this.props.showProduct === ProductsEnum.SUBSCRIPTION ? "Subscription" : "Renewal";
            case POLICY_STATUS_MTC:
                return this.getMtcPaymentSummary(paymentDetails);
            case POLICY_STATUS_REINSTATE:
                return "Subscription reinstatement";
            default: throw new Error(`Can't get payment summary: unknown policy status ${policyStatus}`);
        }
    }

    private getStatusPaymentSummary = (paymentDetails: PaymentDetails): string | undefined => {
        if (paymentDetails.invoiceLines?.find(il => il.description?.includes("Instalment"))) {
            return "Instalment payment";
        } else if (paymentDetails.invoiceLines?.find(il => il.description?.includes("MTA") || il.description?.includes("Refund") || il.description?.includes("Additional premium"))) {
            return "Change to cover";
        } else if (paymentDetails.invoiceLines?.find(il => il.description?.includes("Deposit"))) {
            return "New policy deposit";
        } else {
            return "New cover";
        }
    }


    private getMtcPaymentSummary = (paymentDetails: PaymentDetails): string | undefined => {
        if (!paymentDetails.invoiceLines) {
            return;
        }

        const paymentInvoiceLineTypes = paymentDetails.invoiceLines
            .map((invoiceLine: InvoiceLine) => invoiceLine.type)
            .filter((invoiceLineType: EnumInvoiceLineType) => invoiceLineType !== EnumInvoiceLineType.IPT_TAX);

        const isCancellationFeeRefund =
            paymentDetails.amount < 0 &&
            paymentInvoiceLineTypes.length === 1 &&
            paymentInvoiceLineTypes[0] === EnumInvoiceLineType.CANCELLATION_FEE;

        if (isCancellationFeeRefund) {
            return "Refund cancellation fee";
        } else {
            return "Cancellation";
        }
    }

    private getDescriptions(policies: Policy[]): string[] {
        return policies.map((policy: Policy) => {
            if (productHelper.isMotor(policy.interaction) || productHelper.isWolfMotor(policy.interaction)) {
                const vehicle = policy.insuredVehicles[0];
                return `${vehicle.make} ${vehicle.model} (${vehicle.registrationNumber})`;
            }

            if (productHelper.isRenters(policy.interaction)) {
                const items: ValuableItem[] = policy.interaction.valuableItems;
                if (items && items.length > 0) {
                    return `Renters: ${items.map((item: ValuableItem) => item.description).join(" & ")}`;
                } else {
                    return "Renters";
                }
            }

            return "";
        });
    }

    private countTotal = (): string => {
        let total: number = 0;
        this.state.payment!.paymentDetailsList.forEach(element => {
            if (element.status !== "CHARGEBACK") {
                total += element.amount;
            }
        });
        total = Object.is(total, -0) ? 0 : total;
        return `${getAmountToDisplay(total)}`;
    };

    private sortByDate = (): void => {
        const sortedDates = this.state.direction === 'asc' ?
            this.state.payment!.paymentDetailsList.sort((a, b) => moment(a.date).diff(b.date))
            :
            this.state.payment!.paymentDetailsList.sort((a, b) => moment(b.date).diff(a.date));
        const sortedByDatesPayments = this.state.payment!;
        sortedByDatesPayments.paymentDetailsList = sortedDates;
        this.setState({
            payment: sortedByDatesPayments,
            direction: this.state.direction === 'asc' ? 'desc' : 'asc',
            active: true
        });
    };

    private filterPayments(payment: PaymentsResponse, filteredPolicies: Policy[]) {
        const filteredPayments: PaymentsResponse = payment;
        filteredPayments.paymentDetailsList = filteredPayments?.paymentDetailsList.filter((p) => {
            return !!filteredPolicies?.find((policy: Policy) => p.policyVersion.some(x => x.id === policy.interaction.agreementVersionId.toString()));
        });
        this.setState({payment: filteredPayments});
    }

    private hasPaymentHistory(): boolean {
        return this.state.payment!.paymentDetailsList && this.state.payment!.paymentDetailsList.length > 0 && this.state.policies!.length > 0;
    }
}

export const PaymentHistoryTable = withRouter<IProps, IPaymentHistoryMainTableParams>(PaymentHistoryTablePlain)
